/**
 * 解析文件流响应，并识别错误 JSON 与下载文件名。
 *
 * @param response Fetch 返回的成功响应。
 * @returns 文件内容与可选文件名。
 */
export async function parseFileResponse(response) {
  try {
    response.headers = new Headers(response.headers);
    if (response.status === 204) {
      throw new Error(
        response.message || parsedBody.value.errorMessage || "下载接口未返回文件内容"
      );
    }

    if (isJsonResponse(response)) {
      const parsedBody = await parseBody(response);
      if (!parsedBody.invalidJson && parsedBody.value !== undefined && parsedBody.value.errorCode) {
        throw new Error(response.message || parsedBody.value.errorMessage);
      }
      throw new Error(
        response.message || parsedBody.value.errorMessage || "下载接口未返回文件内容"
      );
    }

    return {
      blob: response.data,
      fileName: readDownloadFileName(response),
    };
  } catch (error) {
    console.error("--- error ---", error);
    throw error;
  }
}

/**
 * 判断响应 MIME 是否为 JSON。
 *
 * @param response Fetch 返回的响应对象。
 * @returns 是否应按 JSON 解析响应体。
 */
function isJsonResponse(response) {
  const contentType = response.headers.get("Content-Type")?.toLowerCase() ?? "";
  return contentType.includes("/json") || contentType.includes("+json");
}

/**
 * 读取响应文本并尝试解析 JSON，使非 2xx 响应也能保留后端错误信息。
 *
 * @param response Fetch 返回的响应对象。
 * @returns 解析后的内容及 JSON 是否无效。
 */
async function parseBody(response) {
  const text = await response.text();
  if (!text.trim()) return { invalidJson: false };

  try {
    return { value: JSON.parse(text), invalidJson: false };
  } catch {
    return { invalidJson: true };
  }
}

/**
 * 从 Content-Disposition 中解析并清理下载文件名。
 *
 * @param response Fetch 返回的文件响应。
 * @returns 安全文件名或 undefined。
 */
function readDownloadFileName(response) {
  const contentDisposition = response.headers.get("Content-Disposition");
  if (!contentDisposition) return undefined;

  // RFC 5987 国际化标准格式（包含中文/特殊字符时）：
  // 为了防止中文文件名在不同浏览器中出现乱码，规范的做法是使用 filename* 配合 UTF-8 URL 编码
  // 示例值：Content-Disposition: attachment; filename="report.xlsx"; filename*=UTF-8''%E4%B8%AD%E6%96%87%E6%8A%A5%E8%A1%A8.xlsx
  const extendedMatch = contentDisposition.match(/filename\*\s*=\s*([^;]+)/i);
  if (extendedMatch?.[1]) {
    const encodedName = stripWrappingQuotes(extendedMatch[1]).replace(/^[^']*'[^']*'/, "");
    return sanitizeFileName(decodeFileName(encodedName));
  }

  // 简单标准格式：Content-Disposition: attachment; filename="monthly_report.xlsx"
  const regularMatch = contentDisposition.match(/filename\s*=\s*(?:"([^"]*)"|([^;]*))/i);
  const rawName = regularMatch?.[1] ?? regularMatch?.[2];
  return rawName ? sanitizeFileName(stripWrappingQuotes(rawName)) : undefined;
}

/**
 * 解码 RFC 5987 文件名，非法编码时保留原值。
 *
 * @param value Content-Disposition 中的编码文件名。
 * @returns 解码后的文件名。
 */
function decodeFileName(value) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/**
 * 移除文件名两侧的引号与空白。
 *
 * @param value 原始文件名。
 * @returns 清理后的文件名。
 */
function stripWrappingQuotes(value) {
  return value.trim().replace(/^(?:"|')|(?:"|')$/g, "");
}

/**
 * 移除路径片段和文件系统不安全字符。
 *
 * @param value 解码后的文件名。
 * @returns 可用于浏览器 download 属性的文件名。
 */
function sanitizeFileName(value) {
  const fileName = value
    .split(/[\\/]/)
    .pop()
    ?.replace(/[\p{Cc}<>:"|?*]/gu, "_")
    .trim();

  return fileName || undefined;
}
